import { createContext, Fragment, useEffect, useState } from 'react';

import ZoomAPI from 'utils/api/zoom';

import { ZoomWebinar } from 'types/zoom';

import FontIcon from 'components/icons';

import HubspotCalendar from './steps/hubspot-calendar';
import StepStartTrialPromo from './steps/start-trial-promo';
import StepVideoDemoPromo from './steps/video-demo-promo';
import StepWebinarPromo from './steps/webinar-promo';
import StepWebinarRegistration from './steps/webinar-registration';
import StepWelcome from './steps/welcome';
import ScheduleDemoWizardConfig from './types/config';
import emitScheduleDemoEvent, {
  EmitScheduleDemoEventProps,
  ScheduleDemoMercuryAction,
} from './util/send-mercury-event';

type EmitEventFn = (props: Omit<EmitScheduleDemoEventProps, 'employeesSize'>) => void

type ScheduleDemoContext = {
  emitEvent: EmitEventFn
}

export const ScheduleDemoWizardContext = createContext<ScheduleDemoContext>({
  emitEvent: () => null,
});

export enum EmployeeCountOption {
  SMALL_BUSINESS = '1 - 20',
  MEDIUM_BUSINESS = '21 - 200',
  LARGE_BUSINESS = '201 - 10,000',
}

enum WizardStep {
  SALES_LARGE_BUSINESS = 'sales-large-business',
  SALES_MEDIUM_BUSINESS = 'sales-medium-business',
  SALES_SMALL_BUSINESS = 'sales-small-business',
  START_TRIAL_PROMO = 'start-trial',
  VIDEO_DEMO_PROMO = 'video-demo-promo',
  WEBINAR = 'webinar',
  WEBINAR_PROMO = 'webinar-promo',
  WELCOME = 'welcome',
}

type ScheduleDemoWizardProps = {
  config: ScheduleDemoWizardConfig
  setModalFooterImageVisibility?: (visible: boolean) => void
}

const ScheduleDemoWizard = (props:ScheduleDemoWizardProps) => {

  const [currentStep, setCurrentStep] = useState<WizardStep>(WizardStep.WELCOME);
  const [employeeCount, setEmployeeCount] = useState<EmployeeCountOption | undefined>();
  const [zoomWebinar, setZoomWebinar] = useState<ZoomWebinar>();

  const emitEvent: EmitEventFn = data => {
    emitScheduleDemoEvent({
      ...data,
      employeesSize: employeeCount,
    });
  };

  const handleBackButton = () => {
    if (currentStep === WizardStep.WEBINAR) {
      emitEvent({
        action: ScheduleDemoMercuryAction.WEBINAR_REGISTRATION_ABANDONED,
      });
    }

    if (employeeCount === EmployeeCountOption.SMALL_BUSINESS || employeeCount === EmployeeCountOption.MEDIUM_BUSINESS || employeeCount === EmployeeCountOption.LARGE_BUSINESS) {
      setToWelcomeStep();
      setEmployeeCount(undefined);
    }
  };

  const setModalFooterImageVisibility = (value: boolean) => {
    if (props.setModalFooterImageVisibility) {
      props.setModalFooterImageVisibility(value);
    }
  };


  const setToWelcomeStep = () => {
    setCurrentStep(WizardStep.WELCOME);
    setEmployeeCount(undefined);
    setModalFooterImageVisibility(true);
  };

  const setToWebinarStep = () => {
    setCurrentStep(WizardStep.WEBINAR);
    setModalFooterImageVisibility(false);
  };

  const setToLargeBusinessStep = () => {
    setCurrentStep(WizardStep.SALES_LARGE_BUSINESS);
    setModalFooterImageVisibility(true);
  };
  const setToMediumBusinessStep = () => {
    setCurrentStep(WizardStep.SALES_MEDIUM_BUSINESS);
    setModalFooterImageVisibility(true);
  };
  const setToSmallBusinessStep = () => {
    setCurrentStep(WizardStep.SALES_SMALL_BUSINESS);
    setModalFooterImageVisibility(true);
  };

  const setToVideoDemoPromoStep = () => {
    setCurrentStep(WizardStep.VIDEO_DEMO_PROMO);
    setModalFooterImageVisibility(true);
  };


  const handleEmployeeCountChange = (count: EmployeeCountOption) => {
    setEmployeeCount(count);

    switch (count) {
      case EmployeeCountOption.SMALL_BUSINESS:
        setToSmallBusinessStep();
        break;
      case EmployeeCountOption.MEDIUM_BUSINESS:
        setToMediumBusinessStep();
        break;
      case EmployeeCountOption.LARGE_BUSINESS:
        setToLargeBusinessStep();
        break;
    }
  };

  useEffect(() => {
    if (employeeCount) {
      emitEvent({
        action: ScheduleDemoMercuryAction.EMPLOYEES_SIZE_SELECTED,
        annotations: { userWasServed: 'webinar' },
      });
    }
  }, [employeeCount]);

  const fetchZoomWebinar = async (webinarId: string | number) => {
    const webinar = await ZoomAPI.GetWebinar(webinarId);
    setZoomWebinar(webinar);
  };

  useEffect(() => {
    emitEvent({
      action: ScheduleDemoMercuryAction.MODAL_OPENED,
    });

    if (props.config.zoomWebinarId !== '') {
      fetchZoomWebinar(props.config.zoomWebinarId);
    }

    return () => {
      emitEvent({
        action: ScheduleDemoMercuryAction.MODAL_CLOSED,
      });
    };
  }, []);

  return (
    <ScheduleDemoWizardContext.Provider value={ { emitEvent } }>
      <div className={ `schedule-demo-wizard ${props.config.customHubspotMeetingUrl ? 'hubspot-calendar' : ''}` }>
        {(currentStep === WizardStep.WEBINAR || currentStep === WizardStep.SALES_SMALL_BUSINESS || currentStep === WizardStep.SALES_MEDIUM_BUSINESS || currentStep === WizardStep.SALES_LARGE_BUSINESS) && (
          <div className="schedule-demo-wizard--step-back">
            <button onClick={ handleBackButton }>
              <FontIcon icon="arrow-left" />
              <span className="is-sr-only">Previous step</span>
            </button>
          </div>
        )}
        {currentStep === WizardStep.WELCOME && !props.config.customHubspotMeetingUrl && (
          <StepWelcome
            copy={ props.config.mainText.copy }
            onEmployeeCountSelect={ handleEmployeeCountChange }
            title={ props.config.mainText.title }
          />
        )}
        {props.config.customHubspotMeetingUrl && <HubspotCalendar url={ props.config.customHubspotMeetingUrl } />}
        {currentStep === WizardStep.SALES_LARGE_BUSINESS && (
          <HubspotCalendar url={ props.config.formUrls.largeBusiness } />
        )}
        {currentStep === WizardStep.SALES_MEDIUM_BUSINESS && (
          <HubspotCalendar url={ props.config.formUrls.mediumBusiness } />
        )}
        {currentStep === WizardStep.SALES_SMALL_BUSINESS && (
          <HubspotCalendar url={ props.config.formUrls.smallBusiness } />
        )}
        {currentStep === WizardStep.WEBINAR && (
          <Fragment>
            {zoomWebinar ? (
              <StepWebinarRegistration
                avatarImageUrl={ props.config.webinarPromoText.avatarImageUrl }
                employeeCount={ employeeCount }
                onRegistration={ setToVideoDemoPromoStep }
                zoomWebinar={ zoomWebinar }
              />
            ) : (
              <p>Loading...</p>
            )}
          </Fragment>
        )}
        {currentStep === WizardStep.START_TRIAL_PROMO && (
          <StepStartTrialPromo
            textConfig={ props.config.freeTrialPromoText }
          />
        )}
        {currentStep === WizardStep.WEBINAR_PROMO && (
          <StepWebinarPromo
            onButtonClick={ setToWebinarStep }
            textConfig={ props.config.webinarPromoText }
          />
        )}
        {currentStep === WizardStep.VIDEO_DEMO_PROMO && (
          <StepVideoDemoPromo
            textConfig={ props.config.videoDemoPromoText }
            wistiaVideoHashedId={ props.config.videoDemoPromoText.wistiaVideoHashedId }
          />
        )}
      </div>
    </ScheduleDemoWizardContext.Provider>
  );
};

export default ScheduleDemoWizard;

